import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import {
  motion,
  motionValue,
  useAnimationFrame,
  useTransform,
  type MotionValue,
} from "framer-motion";

import {
  hashString,
  mulberry32,
} from "../../../utils/cloud-layout/commentCloudLayout";

/**
 * ScaleThroughStage - 固定画面の中を作品が奥から手前へ通り抜けるステージ
 * （GALLERY 検索結果用）。
 *
 * 参考: https://motion-demos-psi.vercel.app/scale-through-scroll/
 * 「画面内に散らばる画像が、スクロールに合わせて鮮明になり、
 * そのまま手前へ拡大して消える無限ループ」を同じ仕組みで再現する。
 *
 * ページ自体はスクロールしない（マウント中は html / body のスクロールを
 * ロック）。wheel / タッチドラッグを「仮想スクロール量」として蓄積し、
 * 指数平滑でなめらかに追従させる。各スロットが位相を 1/slotCount ずつ
 * ずらして循環し、位相 p に応じて:
 *
 * - scale: SCALE_MIN → SCALE_MAX（0.86 → 1.0。登場時点からほぼ実寸で、
 *   ズームは奥行きの気配程度に抑えて常に可読な大きさを保つ）
 * - 可視度 d: 位相の冒頭 8% で 0→1、中間で 1 を維持、末尾 24% で 1→0
 *   （登場はすぐ読める。近づいて消えるフェードアウトはゆっくり長めに）
 * - opacity: smoothstep(d)（フェードイン → フェードアウト）
 * - blur: (1 − d) × 2px（フェードの瞬間だけ薄くボケる。可読性優先）
 * - z-index: 位相 × 400（手前のものほど上）
 *
 * 配置は「画面中心に対する同心円（楕円）リングのパッキング」で決める。
 * アイテムの実寸（width / height）+ 種別ごとの余白から矩形セルを確保し、
 * 画面内・中央の検索 UI（exclusion 矩形）の外・既配置セルと非交差の位置
 * だけを採用するため、アイテム同士や検索 UI との重なりは構造的に起きない。
 * スロット数はビューポート面積の目安と「実際に置けたセル数」で決まる。
 *
 * 1 周した（floor が変わった）スロットは、種別ごとのストリーム（panel =
 * アーティスト / chip = 作品）をカーソルで巡回して次のアイテムへ再利用
 * される。他スロットが表示中のアイテムはスキップするため、同一の作品・
 * アーティストが 1 画面に同時に出ることはない。位置はセル中心 ± セル内の
 * 空きぶんのジッター（スロット・周回・アイテムのシード付き乱数）で決まる。
 *
 * ポインタ位置による視差（手前のものほど大きく動く）も同デモと同じ係数で
 * 適用する。位相・視差は MotionValue を style へ直結して駆動するため、
 * スクロール中の React 再レンダリングは発生しない（再レンダリングは
 * スロットの再利用時のみ）。
 */

export interface ScaleThroughStageItem {
  /** 再利用時の remount 判定に使う一意キー。 */
  key: string;
  /** scale = 1 時点の実寸幅(px)。スロットのボックス幅になる。 */
  width: number;
  /** scale = 1 時点の実寸高さ(px)。セルの衝突判定に使う。 */
  height: number;
  /** 種別。panel = アーティストパネル / chip = 作品チップ。
   * セル寸法・余白・巡回ストリームを種別ごとに分ける。 */
  kind: "panel" | "chip";
  /** スロットに描画する内容。 */
  node: React.ReactNode;
}

/** アイテムを置かない除外矩形（ビューポート px。中央の検索 UI など）。
 * 参照が変わると再パッキングされるため、呼び出し側で量子化して安定した
 * 参照を渡すこと。 */
export interface StageExclusionRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ScaleThroughStageProps {
  items: ScaleThroughStageItem[];
  /** アイテムを置かない除外矩形（中央の検索 UI の実測値）。 */
  exclusion?: StageExclusionRect | null;
  /** 画面下端に出す操作ヒント（例: 「スクロール ↓」）。 */
  hint?: string;
}

/** スロット数の目安（totalTarget）のクランプ。実スロット数はパッキングで
 * 置けたセル数で決まるため、下限を下回ることはある。SLOT_COUNT_MAX は
 * 実スロット数の真の上限で、MotionValue プール / 割当配列の固定長も兼ねる。 */
const SLOT_COUNT_MIN = 5;
const SLOT_COUNT_MAX = 14;
/** スロット 1 個あたりのビューポート面積(px²)。面積 ÷ この値がスロット数の目安。 */
const VIEWPORT_AREA_PER_SLOT_PX2 = 190_000;
/**
 * スロットが 1 周する仮想スクロール量(px)。参考デモ（920px）の半分にして、
 * 半分のスクロール量で同じだけ変化する＝体感の感度を約 2 倍にしている。
 */
const CYCLE_SCROLL_PX = 460;
/**
 * 登場時点（位相 0）のスケール。0 から拡大すると出現時に読めないため、
 * ほぼ実寸から始めてズームは奥行きの気配程度に抑える。
 */
const SCALE_MIN = 0.86;
/**
 * 位相 1.0 時点の最終スケール（消える瞬間の大きさ）。
 * 大きく拡大させる演出は不要（連続して出てくれば十分）のため等倍を上限とする。
 */
const SCALE_MAX = 1;
/** 可視度ランプ: この位相までフェードイン（短く。登場した時点で読める）。 */
const RAMP_IN_PHASE = 0.08;
/**
 * 可視度ランプ: この位相からフェードアウト。手前に近づいて消える演出は
 * 余韻を持たせてゆっくり長めに（位相の 24%）。
 */
const RAMP_OUT_PHASE = 0.76;
/** 可視度 0 時点の最大ブラー(px)。可読性優先でフェード時の気配程度に留める。 */
const BLUR_MAX_PX = 2;
/** wheel 1 イベントで進める仮想スクロール量の上限(px)。 */
const WHEEL_STEP_CLAMP_PX = 180;
/** タッチドラッグ量 → 仮想スクロール量の倍率。 */
const TOUCH_SCROLL_MULTIPLIER = 1.4;
/** 仮想スクロールの指数平滑係数（1ms あたり）。 */
const SCROLL_EASE_PER_MS = 0.014;
/** ポインタ視差の指数平滑係数（1ms あたり）。 */
const POINTER_EASE_PER_MS = 0.009;
/** 視差の基本振幅(px) + 位相比例分(px)。手前ほど大きく動く。 */
const PARALLAX_BASE_PX = 7;
const PARALLAX_PER_PHASE_PX = 32;
/** タブ復帰などの巨大なフレーム間隔をクランプする(ms)。 */
const MAX_FRAME_DELTA_MS = 48;
/** クリックを受け付ける可視度のしきい値（可読ならクリックできる）。 */
const POINTER_ACTIVE_VISIBILITY = 0.3;
/** 位相 1.0 時点の z-index（手前ほど上）。StageHint(500) より下に収める。 */
const SLOT_Z_MAX = 400;

/** パネル（アーティスト）セルの余白(px, 片側)。ステートメントのまわりに
 * 余白を持たせて詰まらないようにする。 */
const PANEL_GAP_PX = 24;
/** チップ（作品）セルの余白(px, 片側)。 */
const CHIP_GAP_PX = 10;
/** 除外矩形（中央検索 UI）へ足す余白(px)。 */
const EXCLUSION_PAD_PX = 16;
/** ビューポート端の余白(px)。セルはこの内側に収める。 */
const STAGE_EDGE_PAD_PX = 8;
/** リング半径の探索ステップ（下限 px とセル高さに対する比）。細かいほど
 * 除外矩形ぎわの狭い帯も取りこぼさない。 */
const RING_STEP_MIN_PX = 28;
const RING_STEP_CELL_RATIO = 0.4;
/** 1 リングあたりの候補数の上限。 */
const RING_CANDIDATES_MAX = 48;
/** リング内の候補角度を散らす黄金角(rad)。同方向への偏りを防ぐ。 */
const GOLDEN_ANGLE_RAD = 2.399963229728653;
/** リング半径の上限（ビューポート高さ比）。四隅までの探索を含む。 */
const RING_RADIUS_MAX_VH_RATIO = 0.75;
/** パッキング入力のビューポート量子化(px)。リサイズのたびに再配置して
 * 飛行中の座標が跳ぶのを防ぎ、バケットが変わったときだけ組み直す。
 * 粗すぎると配置帯の残り高さを食い潰すので控えめに。 */
const VIEWPORT_QUANT_PX = 16;

const clamp = (v: number, min: number, max: number): number =>
  Math.min(Math.max(v, min), max);

/** 位相 p に対する可視度（冒頭 RAMP_IN_PHASE で立ち上がり、
 * RAMP_OUT_PHASE から 1.0 にかけて消える台形）。 */
const visibilityRamp = (p: number): number =>
  p <= RAMP_IN_PHASE
    ? Math.max(p / RAMP_IN_PHASE, 0)
    : p <= RAMP_OUT_PHASE
      ? 1
      : Math.max((1 - p) / (1 - RAMP_OUT_PHASE), 0);

/**
 * スロットの連続サイクル位置（整数部 = 周回 cycle、小数部 = 位相 phase）。
 * スロット間は 1/slotCount ずつ、全体は半ステップ（+0.5）オフセットする:
 * どのスロットも位相 0 ちょうど（opacity 0）から始まらないため、スロットが
 * 1 個だけの検索でも初期表示からアイテムが見える。
 */
const slotCyclePosition = (
  scrollPx: number,
  slot: number,
  slotCount: number,
): number => scrollPx / CYCLE_SCROLL_PX + (slot + 0.5) / slotCount;

// ===== 同心円セルパッキング =====

/** 中心 + 半径で表す矩形（衝突判定用）。 */
interface RectLike {
  cx: number;
  cy: number;
  halfW: number;
  halfH: number;
}

const rectsOverlap = (a: RectLike, b: RectLike): boolean =>
  Math.abs(a.cx - b.cx) < a.halfW + b.halfW &&
  Math.abs(a.cy - b.cy) < a.halfH + b.halfH;

/** 種別ごとのセル仕様。 */
interface StageCellSpec {
  /** その種別のアイテムの最大実寸(px)。 */
  maxW: number;
  maxH: number;
  /** セル外周の余白(px, 片側)。 */
  gap: number;
  /** 置きたいセル数。 */
  target: number;
}

/** パッキングで確定したセル。スロット 1 個に対応する。 */
interface PackedCell {
  kind: "panel" | "chip";
  /** セル中心(px)。 */
  cx: number;
  cy: number;
  /** セル寸法(px)。最大実寸 + 余白 × 2。 */
  cellW: number;
  cellH: number;
  /** 片側余白(px)。ジッター上限の算出に使う。 */
  gap: number;
}

/**
 * 画面中心からの同心円（楕円）リングを内側から走査し、種別ごとのセルを
 * 詰める。panel セルを先に、残余に chip セルを充填する。
 *
 * 各リングは黄金角ステップで候補角度を散らし、候補は
 * (a) ビューポート内へクランプした上で、
 * (b) 除外矩形（中央の検索 UI + 余白）と非交差、
 * (c) 既配置セルと非交差
 * のものだけを採用する。真上・真下だけに並びにくいよう、開始角度は
 * 斜め方向（対角象限）へシードでオフセットする。
 * 同じ入力に対して常に同じ配置になる決定的な生成。
 *
 * 低いウィンドウなどで 1 セルも置けない種別は、余白半減 → 除外矩形なし
 * の順で条件を緩めて再試行する（何も表示されないよりは、検索 UI の背面を
 * 通る従来挙動へ戻る方がよい。アイテム同士の非重なりは常に保つ）。
 */
const buildStageCells = (
  vw: number,
  vh: number,
  exclusion: StageExclusionRect | null,
  panel: StageCellSpec,
  chip: StageCellSpec,
  totalTarget: number,
): PackedCell[] => {
  const cx0 = vw / 2;
  const cy0 = vh / 2;
  // 楕円係数: 半径はビューポート高さ基準、横方向はアスペクト比で伸ばす。
  const xFactor = vw / vh;
  const exclusionRect: RectLike | null = exclusion
    ? {
        cx: exclusion.left + exclusion.width / 2,
        cy: exclusion.top + exclusion.height / 2,
        halfW: exclusion.width / 2 + EXCLUSION_PAD_PX,
        halfH: exclusion.height / 2 + EXCLUSION_PAD_PX,
      }
    : null;
  const accepted: RectLike[] = [];
  const cells: PackedCell[] = [];

  const tryPackKind = (
    kind: "panel" | "chip",
    spec: StageCellSpec,
    gapScale: number,
    useExclusion: boolean,
  ): number => {
    const gap = Math.round(spec.gap * gapScale);
    const cellW = spec.maxW + gap * 2;
    const cellH = spec.maxH + gap * 2;
    const halfW = cellW / 2;
    const halfH = cellH / 2;
    // セルがビューポートに収まらない極端な画面では置けない。
    if (cellW + STAGE_EDGE_PAD_PX * 2 > vw || cellH + STAGE_EDGE_PAD_PX * 2 > vh) {
      return 0;
    }
    const rand = mulberry32(
      hashString(`gallery-stage-pack:${kind}:${vw}x${vh}:${spec.target}`),
    );
    const rStep = Math.max(RING_STEP_MIN_PX, cellH * RING_STEP_CELL_RATIO);
    const rMax = vh * RING_RADIUS_MAX_VH_RATIO;
    // 除外矩形を諦める最終フォールバックでは外側のリングから詰め、
    // 中央の検索 UI との重なりをできるだけ小さくする。
    const radii: number[] = [];
    for (let r = 0; r <= rMax; r += rStep) radii.push(r);
    if (!useExclusion) radii.reverse();
    let placed = 0;
    for (const r of radii) {
      if (placed >= spec.target) break;
      const perimeter = Math.PI * r * (1 + xFactor);
      const candidates =
        r === 0
          ? 1
          : clamp(
              Math.ceil(perimeter / (Math.min(cellW, cellH) * 0.5)),
              8,
              RING_CANDIDATES_MAX,
            );
      // 斜め方向から始めると、中央 UI の真上・真下だけに並びにくい。
      const angleOffset = Math.PI / 4 + rand() * (Math.PI / 2);
      for (let j = 0; j < candidates && placed < spec.target; j += 1) {
        const theta = angleOffset + j * GOLDEN_ANGLE_RAD;
        const px = clamp(
          cx0 + Math.cos(theta) * r * xFactor,
          STAGE_EDGE_PAD_PX + halfW,
          vw - STAGE_EDGE_PAD_PX - halfW,
        );
        const py = clamp(
          cy0 + Math.sin(theta) * r,
          STAGE_EDGE_PAD_PX + halfH,
          vh - STAGE_EDGE_PAD_PX - halfH,
        );
        const rect: RectLike = { cx: px, cy: py, halfW, halfH };
        if (useExclusion && exclusionRect && rectsOverlap(rect, exclusionRect)) {
          continue;
        }
        if (accepted.some((c) => rectsOverlap(rect, c))) continue;
        accepted.push(rect);
        cells.push({ kind, cx: px, cy: py, cellW, cellH, gap });
        placed += 1;
      }
    }
    return placed;
  };

  const packKind = (kind: "panel" | "chip", spec: StageCellSpec): number => {
    if (spec.target <= 0 || spec.maxW <= 0 || spec.maxH <= 0) return 0;
    const attempts: ReadonlyArray<readonly [number, boolean]> = [
      [1, true],
      [0.5, true],
      [0.5, false],
    ];
    for (const [gapScale, useExclusion] of attempts) {
      const placed = tryPackKind(kind, spec, gapScale, useExclusion);
      if (placed > 0) return placed;
    }
    return 0;
  };

  const placedPanels = packKind("panel", panel);
  packKind("chip", {
    ...chip,
    target: Math.min(chip.target, Math.max(0, totalTarget - placedPanels)),
  });

  // 決定的シャッフル: 位相順（スロット index 順）と空間順（内側 → 外側）の
  // 相関を断ち、隣り合う位置が波状に連続して見えるのを防ぐ。
  const orderRand = mulberry32(
    hashString(`gallery-stage-order:${cells.length}:${vw}x${vh}`),
  );
  for (let i = cells.length - 1; i > 0; i -= 1) {
    const j = Math.floor(orderRand() * (i + 1));
    [cells[i], cells[j]] = [cells[j], cells[i]];
  }
  return cells;
};

// ===== Styled =====

/**
 * ビューポート全面の固定ステージ。検索 UI（Hero, z-index: 1）の背面に敷き、
 * アイテムは検索バーの背面を通過する。wheel / タッチはこのレイヤーが受ける
 * （検索 UI の上では通常どおり UI 操作が優先される）。
 */
const StageLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  touch-action: none;
  overscroll-behavior: none;
`;

/** スロットの位置アンカー。中心基準で配置し、サイズは持たない。 */
const SlotAnchor = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

/** スロットの内容ボックス。transform / filter / z-index を MotionValue で駆動。 */
const SlotContent = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  will-change: transform, opacity, filter;
`;

/** 画面下端の操作ヒント。 */
const StageHint = styled.p.attrs({ className: "bodytext-11" })`
  position: absolute;
  bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  color: var(--soremo-light-gray);
  letter-spacing: 0.2em;
  pointer-events: none;
  z-index: 500;
`;

// ===== スロット =====

interface StageSlotProps {
  phase: MotionValue<number>;
  camX: MotionValue<number>;
  camY: MotionValue<number>;
  /** スロット中心のビューポート座標(px)。 */
  x: number;
  y: number;
  width: number;
  children: React.ReactNode;
}

const StageSlot: React.FC<StageSlotProps> = ({
  phase,
  camX,
  camY,
  x,
  y,
  width,
  children,
}) => {
  const scale = useTransform(
    phase,
    (p) => SCALE_MIN + p * (SCALE_MAX - SCALE_MIN),
  );
  const visibility = useTransform(phase, visibilityRamp);
  const opacity = useTransform(visibility, (d) => d * d * (3 - 2 * d));
  const filter = useTransform(
    visibility,
    (d) => `blur(${((1 - d) * BLUR_MAX_PX).toFixed(2)}px)`,
  );
  const zIndex = useTransform(phase, (p) => Math.round(p * SLOT_Z_MAX));
  const pointerEvents = useTransform(visibility, (d) =>
    d > POINTER_ACTIVE_VISIBILITY ? "auto" : "none",
  );

  return (
    <SlotAnchor style={{ left: x, top: y }}>
      <SlotContent
        style={{
          x: camX,
          y: camY,
          scale,
          opacity,
          filter,
          zIndex,
          pointerEvents,
          width,
        }}
      >
        {children}
      </SlotContent>
    </SlotAnchor>
  );
};

// ===== 本体 =====

/** スロットへの割当（表示アイテム + 周回 + ジッター込みの位置）。 */
interface SlotAssignment {
  itemKey: string;
  cycle: number;
  /** セル中心 ± セル内の空きぶんのジッターを含む位置(px)。 */
  x: number;
  y: number;
}

const ScaleThroughStage: React.FC<ScaleThroughStageProps> = ({
  items,
  exclusion = null,
  hint,
}) => {
  const stageRef = useRef<HTMLDivElement>(null);

  // ビューポート寸法（リサイズ追従）。パッキングとスロット数決定に使う。
  const [viewport, setViewport] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 種別ごとのストリーム（巡回順は items の並び順）。
  const panelItems = useMemo(
    () => items.filter((item) => item.kind === "panel"),
    [items],
  );
  const chipItems = useMemo(
    () => items.filter((item) => item.kind === "chip"),
    [items],
  );
  const itemByKey = useMemo(
    () => new Map(items.map((item) => [item.key, item])),
    [items],
  );

  // パッキング入力は量子化（floor）した寸法。実ビューポートの内側に収まる。
  const packW = Math.max(
    VIEWPORT_QUANT_PX,
    Math.floor(viewport.w / VIEWPORT_QUANT_PX) * VIEWPORT_QUANT_PX,
  );
  const packH = Math.max(
    VIEWPORT_QUANT_PX,
    Math.floor(viewport.h / VIEWPORT_QUANT_PX) * VIEWPORT_QUANT_PX,
  );

  // 種別ごとの最大実寸。セル寸法の基準（検索結果が同じ間は不変）。
  const panelMax = useMemo(
    () =>
      panelItems.reduce(
        (acc, item) => ({
          w: Math.max(acc.w, item.width),
          h: Math.max(acc.h, item.height),
        }),
        { w: 0, h: 0 },
      ),
    [panelItems],
  );
  const chipMax = useMemo(
    () =>
      chipItems.reduce(
        (acc, item) => ({
          w: Math.max(acc.w, item.width),
          h: Math.max(acc.h, item.height),
        }),
        { w: 0, h: 0 },
      ),
    [chipItems],
  );

  // ビューポート面積に応じた同時表示数の目安と、種別ごとの目標配分。
  // panel はアイテム比率で配分（クリエイターのみの検索では全部、作品主体の
  // 検索では 1〜2 枚に落ち着く）。chip はパッキング時に残余へ充填される。
  const totalTarget = clamp(
    Math.round((packW * packH) / VIEWPORT_AREA_PER_SLOT_PX2),
    SLOT_COUNT_MIN,
    SLOT_COUNT_MAX,
  );
  const panelTarget =
    panelItems.length > 0
      ? clamp(
          Math.round((totalTarget * panelItems.length) / Math.max(1, items.length)),
          1,
          Math.min(panelItems.length, totalTarget),
        )
      : 0;
  const chipTargetMax = Math.min(chipItems.length, totalTarget);

  // セル配置。量子化ビューポート / 除外矩形 / 最大実寸が変わったときだけ
  // 組み直す（飛行中の座標ジャンプを最小限にする）。
  const cells = useMemo(
    () =>
      buildStageCells(
        packW,
        packH,
        exclusion,
        { maxW: panelMax.w, maxH: panelMax.h, gap: PANEL_GAP_PX, target: panelTarget },
        { maxW: chipMax.w, maxH: chipMax.h, gap: CHIP_GAP_PX, target: chipTargetMax },
        totalTarget,
      ),
    [
      packW,
      packH,
      exclusion,
      panelMax.w,
      panelMax.h,
      panelTarget,
      chipMax.w,
      chipMax.h,
      chipTargetMax,
      totalTarget,
    ],
  );

  // スロットごとの MotionValue（マウント中は同一インスタンスを使い回す）
  const slotMotion = useMemo(
    () =>
      Array.from({ length: SLOT_COUNT_MAX }, () => ({
        phase: motionValue(0),
        camX: motionValue(0),
        camY: motionValue(0),
      })),
    [],
  );

  // 仮想スクロール / ポインタ視差（target へ指数平滑で追従する）
  const scrollTargetRef = useRef(0);
  const scrollValueRef = useRef(0);
  const pointerTargetRef = useRef({ x: 0, y: 0 });
  const pointerValueRef = useRef({ x: 0, y: 0 });

  // スロット → 表示アイテムの割当。周回（cycle）が変わったスロットだけ
  // カーソル巡回で次のアイテムへ再利用する。
  const [assignments, setAssignments] = useState<Array<SlotAssignment | null>>(
    () => Array<SlotAssignment | null>(SLOT_COUNT_MAX).fill(null),
  );
  const assignmentsRef = useRef(assignments);
  const cursorsRef = useRef<Record<"panel" | "chip", number>>({
    panel: 0,
    chip: 0,
  });

  // 最新の cells / ストリームを安定参照するための ref。割当関数を安定化し、
  // items の参照が変わるたびに全リセットされるのを防ぐ（キーが同じなら
  // 割当は維持され、描画内容だけ itemByKey 経由で最新になる）。
  const layoutRef = useRef({ cells, panelItems, chipItems, itemByKey });
  layoutRef.current = { cells, panelItems, chipItems, itemByKey };

  /**
   * スロットへ次のアイテムを割り当てる。種別ストリームをカーソルから巡回し、
   * 他スロットが表示中のキーはスキップする（1 画面内の重複を防ぐ）。
   * 位置はセル中心 ± セル内の空きぶんのジッターで、(スロット, 周回,
   * アイテム) のシード付き乱数により決定的に決まる。
   */
  const takeAssignment = useCallback(
    (
      slot: number,
      cycle: number,
      current: ReadonlyArray<SlotAssignment | null>,
    ): SlotAssignment | null => {
      const layout = layoutRef.current;
      const cell = layout.cells[slot];
      if (!cell) return null;
      const stream = cell.kind === "panel" ? layout.panelItems : layout.chipItems;
      if (stream.length === 0) return null;
      const used = new Set<string>();
      current.forEach((assignment, index) => {
        if (assignment && index !== slot) used.add(assignment.itemKey);
      });
      const cursors = cursorsRef.current;
      let picked: ScaleThroughStageItem | undefined;
      for (let step = 0; step < stream.length; step += 1) {
        const index = (cursors[cell.kind] + step) % stream.length;
        if (!used.has(stream[index].key)) {
          picked = stream[index];
          cursors[cell.kind] = (index + 1) % stream.length;
          break;
        }
      }
      if (!picked) {
        // 全アイテムが他スロットで表示中（ストリーム長 < スロット数の
        // 異常系）。前回のアイテムを維持して重複だけは避ける。
        const prevKey = current[slot]?.itemKey;
        const prevItem = prevKey ? layout.itemByKey.get(prevKey) : undefined;
        picked = prevItem ?? stream[cursors[cell.kind] % stream.length];
      }
      const rand = mulberry32(
        hashString(`gallery-stage:${slot}:${cycle}:${picked.key}`),
      );
      const jitterX = Math.max(0, (cell.cellW - cell.gap * 2 - picked.width) / 2);
      const jitterY = Math.max(0, (cell.cellH - cell.gap * 2 - picked.height) / 2);
      return {
        itemKey: picked.key,
        cycle,
        x: cell.cx + (rand() * 2 - 1) * jitterX,
        y: cell.cy + (rand() * 2 - 1) * jitterY,
      };
    },
    [],
  );

  // セル配置やアイテムのキー構成が変わったら割当を最初から組み直す。
  const itemsSignature = useMemo(
    () => items.map((item) => item.key).join("|"),
    [items],
  );
  useLayoutEffect(() => {
    cursorsRef.current = { panel: 0, chip: 0 };
    const count = cells.length;
    const next = Array<SlotAssignment | null>(SLOT_COUNT_MAX).fill(null);
    for (let slot = 0; slot < count; slot += 1) {
      const continuous = slotCyclePosition(scrollValueRef.current, slot, count);
      next[slot] = takeAssignment(slot, Math.floor(continuous), next);
    }
    assignmentsRef.current = next;
    setAssignments(next);
  }, [cells, itemsSignature, takeAssignment]);

  // 毎フレーム: 平滑化 → 各スロットの位相・視差を MotionValue へ反映。
  // 周回が変わったスロットだけ割当を更新する。
  useAnimationFrame((_, delta) => {
    const dt = Math.min(delta, MAX_FRAME_DELTA_MS);
    const scrollEase = 1 - Math.exp(-dt * SCROLL_EASE_PER_MS);
    const pointerEase = 1 - Math.exp(-dt * POINTER_EASE_PER_MS);
    scrollValueRef.current +=
      (scrollTargetRef.current - scrollValueRef.current) * scrollEase;
    pointerValueRef.current.x +=
      (pointerTargetRef.current.x - pointerValueRef.current.x) * pointerEase;
    pointerValueRef.current.y +=
      (pointerTargetRef.current.y - pointerValueRef.current.y) * pointerEase;

    const slotCount = cells.length;
    let assignmentsChanged = false;
    for (let slot = 0; slot < slotCount; slot += 1) {
      const continuous = slotCyclePosition(
        scrollValueRef.current,
        slot,
        slotCount,
      );
      const cycle = Math.floor(continuous);
      const phase = continuous - cycle;
      const current = assignmentsRef.current[slot];
      if (!current || current.cycle !== cycle) {
        assignmentsRef.current[slot] = takeAssignment(
          slot,
          cycle,
          assignmentsRef.current,
        );
        assignmentsChanged = true;
      }
      const mv = slotMotion[slot];
      mv.phase.set(phase);
      const parallaxDepth = PARALLAX_BASE_PX + phase * PARALLAX_PER_PHASE_PX;
      mv.camX.set(-pointerValueRef.current.x * parallaxDepth);
      mv.camY.set(-pointerValueRef.current.y * parallaxDepth);
    }
    if (assignmentsChanged) setAssignments([...assignmentsRef.current]);
  });

  // wheel / タッチドラッグで仮想スクロールを進める（ステージ上のみ）。
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      // deltaMode 1 (行単位, Firefox) は px 相当へ換算する
      const deltaY = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      scrollTargetRef.current += clamp(
        deltaY,
        -WHEEL_STEP_CLAMP_PX,
        WHEEL_STEP_CLAMP_PX,
      );
    };

    let lastTouchY: number | null = null;
    const onTouchStart = (e: TouchEvent) => {
      lastTouchY = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY;
      if (y == null || lastTouchY == null) return;
      e.preventDefault();
      scrollTargetRef.current += (lastTouchY - y) * TOUCH_SCROLL_MULTIPLIER;
      lastTouchY = y;
    };
    const onTouchEnd = () => {
      lastTouchY = null;
    };

    stage.addEventListener("wheel", onWheel, { passive: false });
    stage.addEventListener("touchstart", onTouchStart, { passive: true });
    stage.addEventListener("touchmove", onTouchMove, { passive: false });
    stage.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      stage.removeEventListener("wheel", onWheel);
      stage.removeEventListener("touchstart", onTouchStart);
      stage.removeEventListener("touchmove", onTouchMove);
      stage.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  // ポインタ視差（タッチは対象外）。検索 UI 上でも追従するよう window で拾う。
  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      pointerTargetRef.current = {
        x: clamp((e.clientX / window.innerWidth) * 2 - 1, -1, 1),
        y: clamp((e.clientY / window.innerHeight) * 2 - 1, -1, 1),
      };
    };
    const resetPointer = () => {
      pointerTargetRef.current = { x: 0, y: 0 };
    };
    window.addEventListener("pointermove", onPointerMove);
    document.documentElement.addEventListener("mouseleave", resetPointer);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      document.documentElement.removeEventListener("mouseleave", resetPointer);
    };
  }, []);

  // ステージ表示中はページ本体のスクロールをロックする（画面は固定のまま）。
  // body だけでは html 要素のスクロールが残る（グローバルヘッダー分などの
  // わずかな縦余りでページがずれる）ため、html / body の両方をロックし、
  // スクロール位置も原点へ戻す。
  useEffect(() => {
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;
    const prev = {
      bodyOverflow: bodyStyle.overflow,
      bodyOverscroll: bodyStyle.overscrollBehavior,
      htmlOverflow: htmlStyle.overflow,
      htmlOverscroll: htmlStyle.overscrollBehavior,
    };
    bodyStyle.overflow = "hidden";
    bodyStyle.overscrollBehavior = "none";
    htmlStyle.overflow = "hidden";
    htmlStyle.overscrollBehavior = "none";
    window.scrollTo(0, 0);
    return () => {
      bodyStyle.overflow = prev.bodyOverflow;
      bodyStyle.overscrollBehavior = prev.bodyOverscroll;
      htmlStyle.overflow = prev.htmlOverflow;
      htmlStyle.overscrollBehavior = prev.htmlOverscroll;
    };
  }, []);

  return (
    <StageLayer ref={stageRef}>
      {cells.map((_cell, slot) => {
        const assignment = assignments[slot];
        const item = assignment
          ? itemByKey.get(assignment.itemKey)
          : undefined;
        if (!assignment || !item) return null;
        return (
          <StageSlot
            key={slot}
            phase={slotMotion[slot].phase}
            camX={slotMotion[slot].camX}
            camY={slotMotion[slot].camY}
            x={assignment.x}
            y={assignment.y}
            width={item.width}
          >
            {/* 周回で中身が入れ替わったら remount して再生状態などをリセット */}
            <React.Fragment key={`${item.key}:${assignment.cycle}`}>
              {item.node}
            </React.Fragment>
          </StageSlot>
        );
      })}
      {hint && <StageHint>{hint}</StageHint>}
    </StageLayer>
  );
};

export default React.memo(ScaleThroughStage);
